import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClubBook } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { StarPicker } from "./Stars";
import { reading } from "@pagemates/shared/copy";

const txt = reading.finishCards;

/** Shown on Now the moment you cross 100%: rate, one line, join the finishers. */
export function FinishLineCard({ clubId, book, embedded }: { clubId: string; book: ClubBook; /** inside the finishing ceremony: no card or heading of its own */ embedded?: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [stars, setStars] = useState<number | null>(null);
  const [reaction, setReaction] = useState("");
  const done = useMutation({
    mutationFn: () => api.finishLine(clubId, book.id, { stars, reaction: reaction.trim() || null }),
    onSuccess: async ({ finishLineId }) => {
      await api.dismissMoment(clubId, book.id).catch(() => undefined); // the ceremony was the moment
      await qc.invalidateQueries({ queryKey: ["books", clubId] });
      void navigate({ to: "/clubs/$clubId/talk", params: { clubId }, search: { book: book.id, thread: finishLineId } });
    },
  });
  return (
    <section className={embedded ? "finish-form stack-sm" : "card finish stack-sm"}>
      {!embedded && <div className="eyebrow">{txt.youFinished}</div>}
      {!embedded && <h2 className="book">{book.book.title}</h2>}
      <div className="row" style={{ gap: 10, flexWrap: "wrap" }}><span className="small muted">{txt.yourRating}</span><StarPicker value={stars} onChange={setStars} /></div>
      <label className="field">
        <span>{txt.firstReactionOneLine}</span>
        <input id="first-reaction" className="input" value={reaction} maxLength={280} placeholder={txt.sayTheThingYou} onChange={(e) => setReaction(e.target.value)} />
      </label>
      {done.isError && <p className="error">{describeError(done.error)}</p>}
      <div className="row" style={{ flexWrap: "wrap" }}>
        <button className="btn primary" disabled={done.isPending} onClick={() => done.mutate()}>{txt.joinTheFinishers}</button>
        <span className="muted small">{txt.bothOptionalYouCan}</span>
      </div>
    </section>
  );
}

/** Shown on Now when the club finished a book you were still reading. */
export function StragglerCard({ clubId, s }: { clubId: string; s: NonNullable<import("@pagemates/shared").ClubBooks["straggler"]> }) {
  const qc = useQueryClient();
  const [giving, setGiving] = useState(false);
  const [stars, setStars] = useState<number | null>(null);
  const [reaction, setReaction] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["books", clubId] });
  const keep = useMutation({ mutationFn: () => api.keepReading(clubId, s.clubBookId), onSuccess: refresh });
  const giveUp = useMutation({ mutationFn: () => api.giveUp(clubId, s.clubBookId, { stars, reaction: reaction.trim() || null }), onSuccess: refresh });
  return (
    <section className="card finish stack-sm">
      <div className="eyebrow">{txt.theClubFinished(s.title)}</div>
      <p>{txt.youReAt}<strong className="num">{s.percent}%</strong>.</p>
      {!giving ? (
        <div className="stack-sm">
          <button className="btn" disabled={keep.isPending} onClick={() => keep.mutate()}><b>{txt.keepReading}</b><span className="muted small" style={{ marginLeft: 8 }}>{txt.nothingChangesTheEnding}</span></button>
          <button className="btn" onClick={() => setGiving(true)}><b>{txt.iMDoneWith}</b><span className="muted small" style={{ marginLeft: 8 }}>{txt.opensTheWholeDiscussion}</span></button>
          <p className="muted small">{txt.the}<Link to="/clubs/$clubId/recap/$bookId" params={{ clubId, bookId: s.clubBookId }}>recap</Link>{txt.isUpEitherWay}</p>
        </div>
      ) : (
        <div className="stack-sm">
          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}><span className="small muted">{txt.ratingIfYouWant}</span><StarPicker value={stars} onChange={setStars} /></div>
          <input id="dnf-reaction" className="input" value={reaction} maxLength={280} placeholder={txt.aLineOnWhy} onChange={(e) => setReaction(e.target.value)} />
          {giveUp.isError && <p className="error">{describeError(giveUp.error)}</p>}
          <div className="row"><button className="btn primary" disabled={giveUp.isPending} onClick={() => giveUp.mutate()}>{txt.markAsDoneWith}</button><button className="btn ghost" onClick={() => setGiving(false)}>{txt.back}</button></div>
        </div>
      )}
      {keep.isError && <p className="error">{describeError(keep.error)}</p>}
    </section>
  );
}

/** "I'm done with it" from your own progress row, any time. */
export function GiveUpInline({ clubId, book, onDone }: { clubId: string; book: ClubBook; onDone: () => void }) {
  const qc = useQueryClient();
  const [stars, setStars] = useState<number | null>(null);
  const [reaction, setReaction] = useState("");
  const giveUp = useMutation({
    mutationFn: () => api.giveUp(clubId, book.id, { stars, reaction: reaction.trim() || null }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["books", clubId] }); onDone(); },
  });
  return (
    <div className="editor">
      <p className="small">{txt.doneWith}<b>{book.book.title}</b>{txt.youLlBeMarked}</p>
      <div className="inline"><span>{txt.ratingIfYouWant}</span><StarPicker value={stars} onChange={setStars} /></div>
      <input id="dnf-inline" className="input" value={reaction} maxLength={280} placeholder={txt.aLineOnWhy} onChange={(e) => setReaction(e.target.value)} />
      {giveUp.isError && <p className="error">{describeError(giveUp.error)}</p>}
      <div className="inline spread"><button type="button" className="btn ghost sm" onClick={onDone}>{txt.cancel}</button><button type="button" className="btn danger sm" disabled={giveUp.isPending} onClick={() => giveUp.mutate()}>{txt.iMDoneWith}</button></div>
    </div>
  );
}
